/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */

import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import {
  OptimizelyContext,
  useDecision as nativeUseDecision
} from '@optimizely/react-sdk'
import { useContext, useEffect } from 'react'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getEnv } from 'utils/env'

interface DecisionEvent {
  campaign_id: string
  experiment_id: string
  variation_id: string
}

interface Input {
  ruleKey: string
  variationKey: string
}

export function getDecisionDetails(
  parsedDatafile: any,
  input: Input
): DecisionEvent {
  const df = parsedDatafile
  const result: DecisionEvent = {
    campaign_id: '',
    experiment_id: '',
    variation_id: ''
  }

  if (!df || !df.experiments) {
    throw new Error('invalid config')
  }

  const experiment = df.experiments.find((e: any) => e.key === input.ruleKey)
  if (!experiment) {
    throw new Error('experiment does not exist')
  }
  result.experiment_id = experiment.id
  result.campaign_id = experiment.layerId

  if (!experiment.variations) {
    throw new Error('invalid experiment object')
  }
  const variation = (experiment.variations as Array<any>).find(
    (v) => v.key === input.variationKey
  )
  if (!variation) {
    throw new Error('variation not assigned to experiment')
  }
  result.variation_id = variation.id
  return result
}

export default function useDecision(
  ...args: Parameters<typeof nativeUseDecision>
): ReturnType<typeof nativeUseDecision> | null {
  const { optimizely } = useContext(OptimizelyContext)
  const urlParams = useUrlParams()

  const [decision, ...rest] = !optimizely
    ? [null, false, false]
    : nativeUseDecision(...args)

  useEffect(() => {
    if (
      decision &&
      decision.enabled &&
      optimizely &&
      urlParams.advertisementId === getEnv('REACT_APP_CONFIG_ID')
    ) {
      const datafile = JSON.parse(
        optimizely.getOptimizelyConfig()?.getDatafile() || '{}'
      )

      const decisionDetails = getDecisionDetails(datafile, {
        ruleKey: decision.ruleKey!,
        variationKey: decision.variationKey!
      })

      const eventProperties: { [k: string]: any } = {
        experiment_name: decision.ruleKey,
        variation_name: decision.variationKey,
        ...decisionDetails
      }

      if (decision.enabled && optimizely) {
        broadcastToParent({
          eventName: RegisteredEventName.tracking,
          data: {
            event: 'Experiment Viewed',
            ...eventProperties,
            context: { Optimizely: { userId: getCookie(COOKIE_KEY_FCID) } }
          }
        })
      }
    }
  }, [decision, optimizely, urlParams.advertisementId])

  if (!decision) {
    return null
  }

  return [decision, ...rest]
}
